import React from 'react';
import { ApiError } from '../../../api/http';
import { statePanelErrorCopy } from '../../StatePanel/StatePanel';
import { AlertIcon } from '../uiIcons';
import PillButton from '../PillButton/PillButton';
import './StateMessage.css';

export type StateMessageKind = 'loading' | 'error' | 'empty' | 'blocked';

export interface StateMessageRetry {
  label?: string;
  onClick?: () => void;
  to?: string;
  /** Action in flight: the pill shows busyLabel and blocks repeat clicks. */
  busy?: boolean;
  busyLabel?: string;
}

export interface StateMessageProps {
  kind: StateMessageKind;
  title?: string;
  message?: string;
  /** Mapped to safe copy through StatePanel's mapping; never rendered raw. */
  error?: ApiError | null;
  retry?: StateMessageRetry;
  /** Assertive announcement only for failures caused by the user's own action. */
  urgent?: boolean;
  /** Skeleton footprint while loading. */
  shape?: 'shelf' | 'form' | 'text';
  /** h1 for a page-level state, h2 (default) inside a page. */
  headingLevel?: 1 | 2;
  className?: string;
  testId?: string;
}

const DEFAULT_TITLES: Record<StateMessageKind, string> = {
  loading: 'Loading',
  error: 'We could not load this yet',
  empty: 'Nothing here yet',
  blocked: 'Not available right now',
};

/**
 * Adapter over StatePanel's safe error mapping for migrated surfaces.
 * Loading renders a skeleton and a polite busy status; errors render an icon,
 * text and a retry pill; empty and blocked render plain text.
 */
const StateMessage: React.FC<StateMessageProps> = ({ kind, title, message, error, retry, urgent = false, shape = 'text', headingLevel = 2, className, testId }) => {
  const copy = statePanelErrorCopy(error);
  const heading = title || copy?.title || DEFAULT_TITLES[kind];
  const body = copy?.message || message;
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const classes = `luma-state luma-state--${kind}${className ? ` ${className}` : ''}`;

  if (kind === 'loading') {
    return (
      <div className={classes} role="status" aria-live="polite" aria-busy="true" data-testid={testId}>
        <Heading className="luma-sr-only">{heading}</Heading>
        <div className={`luma-state__skeleton luma-state__skeleton--${shape}`} aria-hidden="true">
          {shape === 'shelf' ? (
            <>
              <span className="luma-state__bone luma-state__bone--title" />
              <span className="luma-state__bones">
                <span className="luma-state__bone luma-state__bone--art" />
                <span className="luma-state__bone luma-state__bone--art" />
                <span className="luma-state__bone luma-state__bone--art" />
              </span>
            </>
          ) : shape === 'form' ? (
            <>
              <span className="luma-state__bone luma-state__bone--label" />
              <span className="luma-state__bone luma-state__bone--field" />
              <span className="luma-state__bone luma-state__bone--label" />
              <span className="luma-state__bone luma-state__bone--field" />
            </>
          ) : (
            <>
              <span className="luma-state__bone luma-state__bone--title" />
              <span className="luma-state__bone luma-state__bone--line" />
              <span className="luma-state__bone luma-state__bone--line luma-state__bone--short" />
            </>
          )}
        </div>
      </div>
    );
  }

  const isError = kind === 'error';
  const role = isError ? (urgent ? 'alert' : 'status') : undefined;
  const live = isError ? (urgent ? 'assertive' : 'polite') : undefined;
  const retryLabel = retry?.label ?? 'Try again';

  return (
    <div className={classes} role={role} aria-live={live} data-testid={testId}>
      {isError && (
        <span className="luma-state__icon" aria-hidden="true">
          <AlertIcon size={28} />
        </span>
      )}
      <div className="luma-state__copy">
        <Heading className="luma-state__title">{heading}</Heading>
        {body && <p className="luma-state__message">{body}</p>}
      </div>
      {retry && (
        <div className="luma-state__action">
          {retry.to ? (
            <PillButton variant="secondary" to={retry.to}>{retryLabel}</PillButton>
          ) : (
            <PillButton variant="secondary" onClick={retry.onClick} busy={retry.busy} busyLabel={retry.busyLabel}>{retryLabel}</PillButton>
          )}
        </div>
      )}
    </div>
  );
};

export default StateMessage;
